import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SettingConfig, SettingValue } from "../../types/settingsTypes";
import { estimateTokens, validateNumericValue } from "../../utils/settings/settingsHelpers";
import { useDraftCommit } from "../../hooks/useDraftCommit";
import { useToastStore } from "../../stores/ToastStore";
import { SettingsEditorPopover } from "./SettingsEditorPopover";

const numberToString = (v: number): string => v.toString();

// Floor keeps short-range settings aligned across the modal.
const MIN_FIELD_CHARS = 4;
// Ceiling fits the widest realistic numeric setting (seed at 2^31-1 = 10 digits)
// without letting one outlier inflate every numeric row.
const MAX_FIELD_CHARS = 10;
// .settings-input border-box: 24px padding (px-sm both sides) + 2px border + 2px slack for cursor / digit-width variance.
const FIELD_CHROME_PX = 28;
const countDecimalDigits = (n: number): number => {
    if (!Number.isFinite(n) || n === 0) return 0;
    const s = Math.abs(n).toString();
    const eIdx = s.indexOf('e-');
    if (eIdx >= 0) {
        const mantissa = s.slice(0, eIdx);
        const mantissaDecimals = mantissa.includes('.') ? mantissa.split('.')[1].length : 0;
        return mantissaDecimals + Number(s.slice(eIdx + 2));
    }
    return s.includes('.') ? s.split('.')[1].length : 0;
};

const computeNumberFieldWidth = (min?: number, max?: number, step?: number): string => {
    const minVal = min ?? 0;
    const maxVal = max ?? 0;
    const intChars = Math.max(
        Math.trunc(Math.abs(maxVal)).toString().length,
        Math.trunc(Math.abs(minVal)).toString().length,
    ) + (minVal < 0 ? 1 : 0);
    const decimalDigits = countDecimalDigits(step ?? 1);
    const decimalChars = decimalDigits > 0 ? decimalDigits + 1 : 0;
    const contentChars = Math.min(
        Math.max(intChars + decimalChars, MIN_FIELD_CHARS),
        MAX_FIELD_CHARS,
    );
    return `calc(${contentChars}ch + ${FIELD_CHROME_PX}px)`;
};

// Threshold matches the ~35 visible chars of a 320px text-sm input — values
// longer than this overflow the field and benefit from the popover editor.
const TEXT_INPUT_CONFIG = {
    LONG_TEXT_THRESHOLD: 35,
    ANIMATION_CLEANUP_DELAY: 200
} as const;

interface ResetButtonProps {
    onClick: () => void;
}

function ResetButton({ onClick }: ResetButtonProps) {
    return (
        <button
            onClick={onClick}
            className="settings-reset-btn"
            title="Reset to default"
            type="button"
            aria-label="Reset to default"
        >
            <i className="fas fa-undo" />
        </button>
    );
}

/**
 * Props interface for all settings input components.
 * Exported for use in SettingsModal's lookup table typing.
 *
 * Note: Not all props are used by every component (e.g., tokenFlag is Text-only,
 * onChange is unused by Info). This is intentional for the generic lookup table.
 */
/* eslint-disable react/no-unused-prop-types, react/require-default-props */
export interface SettingInputProps {
    setting: SettingConfig;
    value: SettingValue;
    onChange?: (value: SettingValue) => void;
    tokenFlag?: boolean;
    devModeActive?: boolean;
}
/* eslint-enable react/no-unused-prop-types, react/require-default-props */

interface SettingInputRenderProps {
    value: SettingValue;
    onChange: (value: SettingValue) => void;
    isUpdating: boolean;
    disabled: boolean;
}

interface SettingInputBaseProps extends SettingInputProps {
    children: (props: SettingInputRenderProps) => React.ReactNode;
}

// Base wrapper that provides optimistic updates for all input types
// Optimistic updates prevent UI lag by showing changes immediately before persistence completes
function SettingInput({ setting, value: actualValue, onChange, children, devModeActive }: SettingInputBaseProps) {
    const [optimisticValue, setOptimisticValue] = useState(actualValue);
    const [isUpdating, setIsUpdating] = useState(false);

    // Sync optimistic value when actual value changes (from successful persistence)
    useEffect(() => {
        setOptimisticValue(actualValue);
        setIsUpdating(false);
    }, [actualValue]);

    const handleChange = useCallback(async (newValue: SettingValue) => {
        if (!(devModeActive || setting.editable) || !onChange) return;

        setOptimisticValue(newValue);
        setIsUpdating(true);

        try {
            await onChange(newValue);
        } catch (error) {
            // Revert on error to show failure
            setOptimisticValue(actualValue);
            console.error('Failed to update setting:', error);
        } finally {
            setIsUpdating(false);
        }
    }, [setting.editable, onChange, actualValue, devModeActive]);

    return (
        <div className={`relative ${isUpdating ? 'animate-pulse' : ''}`}>
            {children({
                value: optimisticValue,
                onChange: handleChange,
                isUpdating,
                disabled: !(devModeActive || setting.editable)
            })}
        </div>
    );
};

// Long values auto-switch to a read-only preview + popover editor to avoid cramped inline editing.
function Text({ setting, value, onChange, tokenFlag, devModeActive}: SettingInputProps) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [editValue, setEditValue] = useState('');
    const textValue = (value as string) || '';
    const tokenCount = tokenFlag ? estimateTokens(textValue) : 0;
    const placeholderText = setting.label ? `Enter ${setting.label}…` : 'Enter value…';

    const isLongText = textValue.length > TEXT_INPUT_CONFIG.LONG_TEXT_THRESHOLD;

    const openPopover = () => {
        setEditValue(textValue);
        setIsPopoverOpen(true);
    };

    const closePopover = () => {
        setIsPopoverOpen(false);
        setEditValue('');
    };

    const saveChanges = () => {
        if (onChange) {
            onChange(editValue as SettingValue);
        }
        closePopover();
    };

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => (
                <>
                    <div className="settings-control-cluster">
                        {isLongText ? (
                            <>
                                <div className="settings-input-wrapper flex-1 min-w-0 settings-input-overflow-fade">
                                    <input
                                        type="text"
                                        value={textValue}
                                        readOnly
                                        disabled={disabled}
                                        placeholder="Click edit to modify..."
                                        className="settings-input-wide w-full"
                                        title={textValue}
                                    />
                                    {tokenFlag && tokenCount > 0 && (
                                        <div className="settings-token-count">
                                            ~{tokenCount} tokens
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={openPopover}
                                    disabled={disabled}
                                    className="settings-action-btn"
                                    title="Open editor"
                                    type="button"
                                    aria-label="Open text editor"
                                >
                                    <i className="fas fa-edit" />
                                </button>
                            </>
                        ) : (
                            <div className="settings-input-wrapper flex-1 min-w-0">
                                <input
                                    type="text"
                                    value={currentValue as string || ''}
                                    onChange={(e) => handleChange(e.target.value as SettingValue)}
                                    disabled={disabled}
                                    placeholder={placeholderText}
                                    spellCheck={false}
                                    className="settings-input w-full"
                                />
                                {tokenFlag && tokenCount > 0 && (
                                    <div className="settings-token-count">
                                        ~{tokenCount} tokens
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <SettingsEditorPopover
                        isOpen={isPopoverOpen}
                        title={`Edit ${setting.label}`}
                        onCancel={closePopover}
                        onSave={saveChanges}
                    >
                        <textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="settings-popover-field w-full h-64 p-sm resize-none"
                            placeholder={placeholderText}
                            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                        />
                        {tokenFlag && (
                            <div className="mt-sm text-sm text-neutral-300">
                                Characters: {editValue.length} | Estimated tokens: ~{estimateTokens(editValue)}
                            </div>
                        )}
                    </SettingsEditorPopover>
                </>
            )}
        </SettingInput>
    );
};

// Single-value dropdown; normalizes options to {value, label} so callers can pass string[] or SelectOption[].
function Select({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const selectSetting = setting as Extract<SettingConfig, { controlType: 'select' }>;

    const normalizedOptions = useMemo(() => {
        if (!selectSetting.options) return [];
        return selectSetting.options.map(opt => {
            if (typeof opt === 'string') {
                return { value: opt, label: opt };
            }
            return opt;
        });
    }, [selectSetting.options]);

    // Trim only when an option would overflow the cluster max — ~36 chars fits a 320px
    // select after padding/chevron; full label stays available via the title tooltip.
    const MAX_OPTION_LENGTH = 36;
    const trimText = (text: string) =>
        text.length > MAX_OPTION_LENGTH ? `${text.substring(0, MAX_OPTION_LENGTH - 1)}…` : text;

    const selectedOption = normalizedOptions.find(opt => opt.value === value);
    const displayLabel = selectedOption?.label || value as string || '';

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => (
                <div className="flex items-center gap-xs">
                    <div className="relative group">
                        <select
                            value={currentValue as string || ''}
                            onChange={(e) => handleChange(e.target.value as SettingValue)}
                            disabled={disabled}
                            className="settings-select appearance-none pr-8 max-w-[var(--settings-control-width)]"
                            title={displayLabel}
                        >
                            {normalizedOptions.map(opt => (
                                <option
                                    key={opt.value}
                                    value={opt.value}
                                    title={opt.label}
                                >
                                    {trimText(opt.label)}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral-300">
                            <i className="fas fa-chevron-down text-md group-hover:text-neutral-100" />
                        </div>
                    </div>
                </div>
            )}
        </SettingInput>
    );
};

// Multi-select via toggleable button tags; value is the array of selected option strings.
function MultiSelect({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const multiSelectSetting = setting as Extract<SettingConfig, { controlType: 'multiSelect' }>;

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => {
                const arrayValue = Array.isArray(currentValue) ? currentValue : [];

                const handleOptionClick = (optionValue: string) => {
                    if (disabled) return; // redundancy

                    const newSelectedValues = [...arrayValue];
                    const index = newSelectedValues.indexOf(optionValue);

                    if (index > -1) {
                        newSelectedValues.splice(index, 1); // Deselect
                    } else {
                        newSelectedValues.push(optionValue); // Select
                    }
                    handleChange(newSelectedValues as SettingValue);
                };

                return (
                    <div className={`settings-multiselect ${disabled ? 'opacity-60' : ''}`}>
                        {multiSelectSetting.options?.map(opt => (
                            <button
                                type="button"
                                key={opt}
                                className="settings-multiselect-tag"
                                data-selected={arrayValue.includes(opt)}
                                onClick={() => handleOptionClick(opt)}
                                disabled={disabled}
                                aria-pressed={arrayValue.includes(opt)}
                            >
                                {opt}
                            </button>
                        ))}
                        {(multiSelectSetting.options?.length === 0) && (
                            <span className="text-neutral-400 text-sm">No options available.</span>
                        )}
                    </div>
                );
            }}
        </SettingInput>
    );
};

// Two-button toggle for binary choices: more prominent than a checkbox for high-signal settings (e.g. theme).
function Switch({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const switchSetting = setting as Extract<SettingConfig, { controlType: 'switch' }>;
    if (!switchSetting.optionA || !switchSetting.optionB) return null;

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => {
                // Ensure currentValue is never null/undefined for comparison
                const safeCurrentValue = currentValue !== null && currentValue !== undefined ? currentValue : switchSetting.optionB.value;

                return (
                    <div className="settings-switch">
                        <button
                            className="settings-switch-option"
                            data-active={safeCurrentValue === switchSetting.optionA.value}
                            onClick={() => handleChange(switchSetting.optionA!.value)}
                            disabled={disabled}
                            type="button"
                        >
                            {switchSetting.optionA.label}
                        </button>
                        <button
                            className="settings-switch-option"
                            data-active={safeCurrentValue === switchSetting.optionB.value}
                            onClick={() => handleChange(switchSetting.optionB!.value)}
                            disabled={disabled}
                            type="button"
                        >
                            {switchSetting.optionB.label}
                        </button>
                    </div>
                );
            }}
        </SettingInput>
    );
};

// Animated check on toggle gives quick visual feedback for state changes.
function Checkbox({ setting, value: propValue, onChange, devModeActive }: SettingInputProps) {
    const [playAnimation, setPlayAnimation] = useState(false);
    const prevValueRef = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        const currentValue = propValue as boolean || false;
        const previousValue = prevValueRef.current;
        prevValueRef.current = currentValue;

        // Animation only triggers on false->true transition to avoid distracting users
        if (currentValue === true && previousValue === false) {
            setPlayAnimation(true);
            // Remove animation class after completion to allow future animations
            const timer = setTimeout(() => {
                setPlayAnimation(false);
            }, TEXT_INPUT_CONFIG.ANIMATION_CLEANUP_DELAY);
            return () => {
                clearTimeout(timer);
            };
        }

        return undefined;
    }, [propValue]);

    return (
        <SettingInput setting={setting} value={propValue} onChange={onChange} devModeActive={devModeActive}>
            {({ value: valueFromParent, onChange: handleChangeFromParent, disabled }) => {
                const isChecked = valueFromParent as boolean || false;

                const inputId = `checkbox-${setting.key}`;
                return (
                    <label className="flex items-center cursor-pointer" htmlFor={inputId}>
                        <input
                            id={inputId}
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                                handleChangeFromParent(e.target.checked);
                            }}
                            disabled={disabled}
                            className="sr-only"
                        />
                        <div
                            className={`settings-checkbox ${playAnimation ? 'settings-checkbox-animate' : ''}`}
                            data-checked={isChecked}
                        >
                            {isChecked && (
                                <i className="fas fa-check text-neutral-white text-xs" />
                            )}
                        </div>
                    </label>
                );
            }}
        </SettingInput>
    );
};

// Read-only path field paired with Electron's native directory dialog — prevents users from entering invalid paths by hand.
function DirectoryPicker({ setting, value, onChange, devModeActive }: SettingInputProps) {
    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
        {({ value: currentValue, onChange: handleChange, disabled }) => {
            const handleClick = async () => {
                if (disabled) return;
                try {
                    if (!window.electron) return;
                    const result = await window.electron.showDirectoryPicker();
                    if (result) {
                        handleChange(result as SettingValue);
                    }
                } catch (error) {
                    console.error('Error selecting directory:', error);
                }
            };

            return (
                <div className="settings-directory-picker">
                    <input
                        type="text"
                        value={currentValue as string || ''}
                        readOnly
                        disabled={disabled}
                        placeholder="No directory selected"
                        className="settings-directory-input"
                        onClick={!disabled ? handleClick : undefined}
                    />
                    <button
                        onClick={handleClick}
                        disabled={disabled}
                        className="settings-action-btn"
                        title="Browse for directory"
                        type="button"
                        aria-label="Browse for directory"
                    >
                        <i className="fas fa-folder-open" />
                    </button>
                </div>
            );
        }}
    </SettingInput>
    );
}

// Toggle visibility lets users verify input without leaving sensitive values exposed by default.
function SecureInput({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => (
                <div className="settings-secure-input">
                    <input
                        type={showPassword ? "text" : "password"}
                        value={currentValue as string || ''}
                        onChange={(e) => handleChange(e.target.value as SettingValue)}
                        disabled={disabled}
                        placeholder="Enter secure value..."
                        className="settings-input min-w-[200px] pr-10"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={disabled}
                        aria-label="Toggle password visibility"
                        className="settings-secure-toggle"
                    >
                        <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                    </button>
                </div>
            )}
        </SettingInput>
    );
};

// Slider for rough adjustment, number field for fine-tuning — paired so users get both.
function DualInput({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const dualSetting = setting as Extract<SettingConfig, { controlType: 'dualInput' }>;
    const [tempValue, setTempValue] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const safeNumericValue = typeof value === 'number' ? value : (dualSetting.default as number || 0);
    const disabled = !(devModeActive || setting.editable);

    const {constraints} = dualSetting;
    const min = constraints?.min ?? 0;
    const max = constraints?.max ?? 100;
    const step = constraints?.step ?? 1;
    const fieldWidth = computeNumberFieldWidth(min, max, step);

    useEffect(() => {
        setTempValue(safeNumericValue);
    }, [safeNumericValue]);

    const validate = useCallback((draft: string): number => {
        const parsed = parseFloat(draft);
        if (draft === '' || Number.isNaN(parsed)) return safeNumericValue;
        return validateNumericValue(parsed, min, max, step);
    }, [safeNumericValue, min, max, step]);

    const { draft, setDraft, commit, revert } = useDraftCommit<string, number>({
        value: safeNumericValue,
        onChange: onChange as (value: number) => void,
        serialize: numberToString,
        validate,
    });

    // Calculate fill percentage for the slider
    const fillPercentage = ((isDragging ? tempValue : safeNumericValue) - min) / (max - min) * 100;

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = parseFloat(e.target.value);
        setTempValue(newValue);
    };

    const handleSliderMouseDown = () => {
        setIsDragging(true);
    };

    const handleSliderMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            commit(tempValue.toString());
        }
    }, [isDragging, tempValue, commit]);

    const handleQuantityClick = (increment: boolean) => {
        const newValue = increment ? safeNumericValue + step : safeNumericValue - step;
        commit(newValue.toString());
    };

    // Global mouse up handler to catch if mouse is released outside the slider
    useEffect(() => {
        if (isDragging) {
            const handleGlobalMouseUp = () => handleSliderMouseUp();
            document.addEventListener('mouseup', handleGlobalMouseUp);
            return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
        }
        return undefined;
    }, [isDragging, handleSliderMouseUp]);

    return (
        <div className="settings-dual-input">
            <div className="settings-dual-slider">
                <div className="settings-slider-container">
                    <div
                        className="settings-slider-fill"
                        style={{ width: `${fillPercentage}%` }}
                    />
                    <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={isDragging ? tempValue : safeNumericValue}
                        onChange={handleSliderChange}
                        onMouseDown={handleSliderMouseDown}
                        onMouseUp={handleSliderMouseUp}
                        onTouchStart={handleSliderMouseDown}
                        onTouchEnd={handleSliderMouseUp}
                        disabled={disabled}
                        className="settings-slider"
                    />
                </div>
            </div>
            <div className="settings-dual-controls">
                <div className="settings-number-wrapper settings-input-wrapper">
                    <input
                        type="number"
                        value={isDragging ? tempValue : draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => commit()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commit();
                            else if (e.key === 'Escape') revert();
                        }}
                        min={min}
                        max={max}
                        step={step}
                        disabled={disabled}
                        className="settings-dual-number"
                        style={{ width: fieldWidth, minWidth: fieldWidth }}
                    />
                    {constraints?.unit && (
                        <span className="settings-unit">{constraints.unit}</span>
                    )}
                </div>
                <div className="settings-dual-buttons">
                    <button
                        onClick={() => handleQuantityClick(true)}
                        disabled={disabled || safeNumericValue >= max}
                        className="settings-dual-button"
                        type="button"
                        aria-label="Increment value"
                    >
                        +
                    </button>
                    <button
                        onClick={() => handleQuantityClick(false)}
                        disabled={disabled || safeNumericValue <= min}
                        className="settings-dual-button"
                        type="button"
                        aria-label="Decrement value"
                    >
                        −
                    </button>
                </div>
            </div>
        </div>
    );
};

// Number input for numeric values without slider - simpler interface for number-only inputs
function NumberInput({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const numberSetting = setting as Extract<SettingConfig, { controlType: 'numberInput' }>;

    const numericValue = typeof value === 'number' ? value : (numberSetting.default as number || 0);
    const disabled = !(devModeActive || setting.editable);

    const {constraints} = numberSetting;
    const min = constraints?.min;
    const max = constraints?.max;
    const step = constraints?.step ?? 1;
    const fieldWidth = computeNumberFieldWidth(min, max, step);

    const validate = useCallback((draft: string): number => {
        if (draft === '') return numericValue;
        const parsed = parseFloat(draft);
        if (Number.isNaN(parsed)) return numericValue;
        return validateNumericValue(parsed, min, max, step);
    }, [numericValue, min, max, step]);

    const { draft, setDraft, commit, revert } = useDraftCommit<string, number>({
        value: numericValue,
        onChange: onChange as (value: number) => void,
        serialize: numberToString,
        validate,
    });

    const handleQuantityClick = (increment: boolean) => {
        const newValue = increment ? numericValue + step : numericValue - step;
        commit(newValue.toString());
    };

    return (
        <div className="settings-number-input">
            <div className="settings-number-controls">
                <div className="settings-number-wrapper">
                    <input
                        type="number"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => commit()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commit();
                            else if (e.key === 'Escape') revert();
                        }}
                        min={min}
                        step={step}
                        disabled={disabled}
                        className="settings-number-input-field"
                        style={{ width: fieldWidth, minWidth: fieldWidth }}
                    />
                    {constraints?.unit && (
                        <span className="settings-unit">{constraints.unit}</span>
                    )}
                </div>
                <div className="settings-number-buttons">
                    <button
                        onClick={() => handleQuantityClick(true)}
                        disabled={disabled || (max !== undefined && numericValue >= max)}
                        className="settings-number-button"
                        type="button"
                        aria-label="Increment value"
                    >
                        +
                    </button>
                    <button
                        onClick={() => handleQuantityClick(false)}
                        disabled={disabled || (min !== undefined && numericValue <= min)}
                        className="settings-number-button"
                        type="button"
                        aria-label="Decrement value"
                    >
                        −
                    </button>
                </div>
            </div>
        </div>
    );
};

// List input: read-only inline summary opens a popover with one row per item.
// Each row is its own bounded input with a delete button; "Add" appends a row.
// Pasting newline- or comma-separated text into a row splits across multiple rows.
function ListTextbox({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const listValue = Array.isArray(value) ? value : [];
    const placeholderText = setting.label ? `Enter ${setting.label}…` : 'Enter value…';
    const showToast = useToastStore(s => s.showToast);

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [editItems, setEditItems] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const lastRowRef = useRef<HTMLInputElement | null>(null);
    const focusLastOnRender = useRef(false);

    const summaryText = listValue.join(', ');
    const disabled = !(devModeActive || setting.editable);

    const openPopover = () => {
        if (disabled) return;
        setEditItems(listValue.length > 0 ? [...listValue] : ['']);
        setIsPopoverOpen(true);
    };

    const closePopover = () => {
        setIsPopoverOpen(false);
        setEditItems([]);
    };

    const saveChanges = async () => {
        const cleaned = editItems.map(s => s.trim()).filter(s => s.length > 0);
        if (!onChange) {
            closePopover();
            return;
        }
        setIsSaving(true);
        try {
            await onChange(cleaned);
            closePopover();
        } catch (error) {
            console.error('Failed to update list setting:', error);
            showToast(`Failed to save ${setting.label || 'list'}`, 'error', { timeout: 3000 });
        } finally {
            setIsSaving(false);
        }
    };

    const updateItem = (index: number, next: string) => {
        setEditItems(prev => prev.map((v, i) => (i === index ? next : v)));
    };

    const addItem = () => {
        focusLastOnRender.current = true;
        setEditItems(prev => [...prev, '']);
    };

    const removeItem = (index: number) => {
        setEditItems(prev => prev.filter((_, i) => i !== index));
    };

    // Splits pasted multi-value text (newlines or commas) across rows starting at index.
    const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text');
        if (!/[\n,]/.test(text)) return;
        e.preventDefault();
        const parts = text
            .split(/[\n,]/)
            .map(s => s.trim().replace(/^["']|["']$/g, ''))
            .filter(s => s.length > 0);
        if (parts.length === 0) return;
        setEditItems(prev => {
            const next = [...prev];
            next.splice(index, 1, ...parts);
            return next;
        });
    };

    useEffect(() => {
        if (focusLastOnRender.current && lastRowRef.current) {
            lastRowRef.current.focus();
            focusLastOnRender.current = false;
        }
    }, [editItems.length]);

    return (
        <>
            <div className="settings-control-cluster">
                <div className="settings-input-wrapper flex-1 min-w-0">
                    <input
                        type="text"
                        value={summaryText}
                        readOnly
                        disabled={disabled}
                        onClick={openPopover}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openPopover();
                            }
                        }}
                        placeholder={placeholderText}
                        title={listValue.length > 1 ? listValue.join('\n') : undefined}
                        className={`settings-input w-full ${disabled ? '' : 'cursor-pointer'}`}
                    />
                </div>
                <button
                    onClick={openPopover}
                    disabled={disabled}
                    className="settings-action-btn"
                    title="Open editor"
                    type="button"
                    aria-label="Open list editor"
                >
                    <i className="fas fa-edit" />
                </button>
            </div>

            <SettingsEditorPopover
                isOpen={isPopoverOpen}
                title={`Edit ${setting.label}`}
                onCancel={closePopover}
                onSave={saveChanges}
                isSaving={isSaving}
            >
                <div className="flex flex-col gap-xs">
                    {editItems.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index} className="flex items-center gap-xs">
                            <span className="text-xs text-neutral-400 w-6 text-right select-none flex-shrink-0">
                                {index + 1}.
                            </span>
                            <input
                                ref={index === editItems.length - 1 ? lastRowRef : undefined}
                                type="text"
                                value={item}
                                onChange={(e) => updateItem(index, e.target.value)}
                                onPaste={(e) => handlePaste(index, e)}
                                placeholder={placeholderText}
                                spellCheck={false}
                                className="settings-popover-field flex-1 min-w-0 px-sm py-xs font-mono"
                            />
                            <button
                                onClick={() => removeItem(index)}
                                className="settings-action-btn flex-shrink-0"
                                type="button"
                                title="Remove"
                                aria-label={`Remove item ${index + 1}`}
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={addItem}
                        type="button"
                        className="btn-ghost mt-xs self-start"
                    >
                        <i className="fas fa-plus mr-xs" />
                        Add
                    </button>
                </div>
            </SettingsEditorPopover>
        </>
    );
};

// Object-valued setting where each property is its own boolean toggle.
function MultiCheckbox({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const multiCheckboxSetting = setting as Extract<SettingConfig, { controlType: 'multiCheckbox' }>;

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => {
                const objectValue = typeof currentValue === 'object' && currentValue !== null ? currentValue : {};

                const handleCheckboxChange = (key: string, checked: boolean) => {
                    if (disabled) return;

                    const newValue = {
                        ...objectValue,
                        [key]: checked
                    };
                    handleChange(newValue as SettingValue);
                };

                return (
                    <div className={`flex flex-wrap gap-xs ${disabled ? 'opacity-60' : ''}`}>
                        {multiCheckboxSetting.checkboxOptions?.map(option => (
                            <div key={option.key} className="flex items-center gap-xs" title={option.tooltip}>
                                <label className="settings-item-badge" htmlFor={`${setting.key}.${option.key}`}>{option.label}:</label>
                                <Checkbox
                                    setting={{
                                        ...setting,
                                        key: `${setting.key}.${option.key}`,
                                        label: option.label,
                                    }}
                                    value={(objectValue as any)[option.key] ?? option.default}
                                    onChange={(checked) => handleCheckboxChange(option.key, checked as boolean)}
                                    devModeActive={devModeActive}
                                />
                            </div>
                        ))}
                    </div>
                );
            }}
        </SettingInput>
    );
};

// Grouped accordion variant of MultiCheckbox.
// Options with a `group` field are collected into collapsible sections; options
// without a group render flat before the grouped sections (backwards compat).
// Groups default to collapsed to keep the panel compact.
function MultiCheckboxGrouped({ setting, value, onChange, devModeActive }: SettingInputProps) {
    const multiCheckboxSetting = setting as Extract<SettingConfig, { controlType: 'multiCheckbox' }>;
    const options = useMemo(
        () => multiCheckboxSetting.checkboxOptions ?? [],
        [multiCheckboxSetting.checkboxOptions],
    );

    // Derive the stable ordered list of group names from the option array.
    const groupNames = useMemo(() => {
        const seen = new Set<string>();
        const ordered: string[] = [];
        options.forEach((opt) => {
            if (opt.group && !seen.has(opt.group)) {
                seen.add(opt.group);
                ordered.push(opt.group);
            }
        });
        return ordered;
    }, [options]);

    // Each group is collapsed by default.
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(groupNames.map((g) => [g, false])),
    );

    const toggleGroup = (group: string) => {
        setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
    };

    return (
        <SettingInput setting={setting} value={value} onChange={onChange} devModeActive={devModeActive}>
            {({ value: currentValue, onChange: handleChange, disabled }) => {
                const objectValue =
                    typeof currentValue === 'object' && currentValue !== null
                        ? (currentValue as Record<string, boolean>)
                        : {};

                const handleCheckboxChange = (key: string, checked: boolean) => {
                    if (disabled) return;
                    handleChange({ ...objectValue, [key]: checked } as any);
                };

                // Options without a group render flat at the top.
                const ungrouped = options.filter((o) => !o.group);

                return (
                    <div className={`flex flex-col gap-xs w-full ${disabled ? 'opacity-60' : ''}`}>
                        {/* Flat (ungrouped) options */}
                        {ungrouped.length > 0 && (
                            <div className="flex flex-wrap gap-xs">
                                {ungrouped.map((option) => (
                                    <div key={option.key} className="flex items-center gap-xs" title={option.tooltip}>
                                        <label className="settings-item-badge" htmlFor={`${setting.key}.${option.key}`}>
                                            {option.label}:
                                        </label>
                                        <Checkbox
                                            setting={{ ...setting, key: `${setting.key}.${option.key}`, label: option.label }}
                                            value={objectValue[option.key] ?? option.default}
                                            onChange={(checked) => handleCheckboxChange(option.key, checked as boolean)}
                                            devModeActive={devModeActive}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Grouped accordion sections */}
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-xs">
                        {groupNames.map((group) => {
                            const groupOptions = options.filter((o) => o.group === group);
                            const isOpen = openGroups[group] ?? false;
                            return (
                                <div key={group} className="border border-neutral-500 rounded-medium overflow-hidden">
                                    <button
                                        type="button"
                                        className="w-full flex items-center justify-between px-sm py-xs text-xs font-medium text-neutral-200 bg-neutral-600 hover:bg-neutral-500 transition-colors"
                                        onClick={() => toggleGroup(group)}
                                        aria-expanded={isOpen}
                                    >
                                        <span>{group}</span>
                                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-neutral-400`} />
                                    </button>
                                    {isOpen && (
                                        <div className="flex flex-wrap gap-xs p-sm bg-neutral-600">
                                            {groupOptions.map((option) => (
                                                <div key={option.key} className="flex items-center gap-xs" title={option.tooltip}>
                                                    <label className="settings-item-badge" htmlFor={`${setting.key}.${option.key}`}>
                                                        {option.label}:
                                                    </label>
                                                    <Checkbox
                                                        setting={{ ...setting, key: `${setting.key}.${option.key}`, label: option.label }}
                                                        value={objectValue[option.key] ?? option.default}
                                                        onChange={(checked) => handleCheckboxChange(option.key, checked as boolean)}
                                                        devModeActive={devModeActive}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    </div>
                );
            }}
        </SettingInput>
    );
}

// Action-only setting: renders a single button that fires onChange; the truthy `value` flag drives the loading state.
function InfoAction({ setting, value, onChange }: SettingInputProps) {
    if (setting.controlType !== 'infoAction') return null;
    const isLoading = Boolean(value);
    const { actionLabel, actionIcon, loadingLabel } = setting;

    return (
        <button
            type="button"
            disabled={isLoading}
            onClick={() => onChange?.(undefined)}
            className={
                isLoading
                    ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed px-6 py-2 rounded-small text-sm font-medium border-0'
                    : 'bg-red-600 hover:bg-red-500 text-neutral-100 px-6 py-2 rounded-small text-sm font-medium border-0 cursor-pointer transition-colors duration-200'
            }
        >
            {isLoading ? (
                <>
                    <i className="fas fa-spinner fa-spin mr-xs" />
                    {loadingLabel ?? actionLabel}
                </>
            ) : (
                <>
                    {actionIcon && <i className={`${actionIcon} mr-xs`} />}
                    {actionLabel}
                </>
            )}
        </button>
    );
}

// Read-only display for computed values, system info, or non-editable configuration.
function Info({ setting: _setting, value }: SettingInputProps) {
    const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || 'Not set');

    return (
        <div className="settings-info">
            <div className="settings-info-value">
                <span className="settings-info-text">
                    {displayValue}
                </span>
            </div>
            <div className="w-5 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-info-circle text-neutral-400 text-sm" title="Read-only setting" />
            </div>
        </div>
    );
};

export const SettingsInputs = {
  ResetButton,
  Text,
  Select,
  MultiSelect,
  Switch,
  Checkbox,
  MultiCheckbox,
  MultiCheckboxGrouped,
  NumberInput,
  DirectoryPicker,
  SecureInput,
  DualInput,
  ListTextbox,
  Info,
  InfoAction,
};
